import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, FileText, Settings, LogOut, Menu, X,
  ChevronDown, Bell, Building2, HelpCircle, Plus
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';

const InvoicePortalLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const menuItems = [
    { name: t('inv_portal_dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/invoices/dashboard' },
    { name: t('inv_title') || 'Invoices', icon: FileText, path: '/invoices/manage' },
    { name: t('shop_nav_settings') || 'Settings', icon: Settings, path: '/invoices/settings' },
  ];

  const handleLogout = () => { logout(); navigate('/'); };
  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="invoice-portal-layout">
      {/* Sidebar - Desktop */}
      <aside className={`fixed inset-y-0 left-0 z-50 bg-[#1e3a5f] transition-all duration-300 hidden lg:block ${sidebarOpen ? 'w-64' : 'w-20'}`}>
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <Link to="/invoices/dashboard" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
              <img src="/logo.png" alt="VendFactura" className="h-14 w-auto object-contain" />
            </div>
            {sidebarOpen && <span className="text-white/70 text-sm font-medium">{t('inv_portal_title') || 'Invoice Portal'}</span>}
          </Link>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="text-white/60 hover:text-white p-1">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {sidebarOpen && (
          <div className="px-4 py-4 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/10 rounded-sm flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.name || 'User'}</p>
                <p className="text-white/50 text-xs truncate">{user?.email}</p>
              </div>
            </div>
          </div>
        )}

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link key={item.path} to={item.path}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                isActive(item.path) ? 'bg-[#22c55e] text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
              title={!sidebarOpen ? item.name : undefined}>
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{item.name}</span>}
            </Link>
          ))}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-3 py-2 text-white/70 hover:text-red-400 transition-colors">
            <LogOut className="w-5 h-5" />
            {sidebarOpen && <span>{t('shop_nav_logout') || 'Log Out'}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-[#1e3a5f] z-50 flex items-center justify-between px-4">
        <button onClick={() => setMobileMenuOpen(true)} className="text-white p-2"><Menu className="w-6 h-6" /></button>
        <Link to="/invoices/dashboard"><img src="/logo.png" alt="V" className="h-10 w-auto object-contain" /></Link>
        <button className="text-white p-2 relative"><Bell className="w-6 h-6" /></button>
      </header>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="lg:hidden fixed inset-0 bg-black/50 z-50" onClick={() => setMobileMenuOpen(false)} />
            <motion.aside initial={{ x: -280 }} animate={{ x: 0 }} exit={{ x: -280 }}
              className="lg:hidden fixed inset-y-0 left-0 w-72 bg-[#1e3a5f] z-50">
              <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
                <img src="/logo.png" alt="VendFactura" className="h-10 w-auto object-contain" />
                <button onClick={() => setMobileMenuOpen(false)} className="text-white p-2"><X className="w-6 h-6" /></button>
              </div>
              <nav className="p-4 space-y-1">
                {menuItems.map((item) => (
                  <Link key={item.path} to={item.path} onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                      isActive(item.path) ? 'bg-[#22c55e] text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                    }`}>
                    <item.icon className="w-5 h-5" /><span>{item.name}</span>
                  </Link>
                ))}
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className={`transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'} pt-16 lg:pt-0`}>
        <header className="hidden lg:flex h-16 bg-white border-b border-slate-200 items-center justify-between px-6">
          <h1 className="text-lg font-semibold text-[#1e3a5f]">
            {menuItems.find(item => isActive(item.path))?.name || 'Dashboard'}
          </h1>
          <div className="flex items-center gap-4">
            <div className="relative">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-sm hover:bg-slate-100">
                <div className="w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">{user?.name?.charAt(0) || 'U'}</span>
                </div>
                <span className="text-sm font-medium text-slate-700">{user?.name || 'User'}</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
              <AnimatePresence>
                {userMenuOpen && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 shadow-lg py-2 z-50">
                    <Link to="/invoices/settings" className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
                      onClick={() => setUserMenuOpen(false)}>{t('shop_nav_settings') || 'Settings'}</Link>
                    <button onClick={handleLogout} className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-slate-100">
                      {t('shop_nav_logout') || 'Log Out'}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>
        <div className="p-6 pb-20 lg:pb-6">{children}</div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 safe-area-bottom" data-testid="invoice-mobile-bottom-nav">
        <div className="flex items-center justify-around h-16">
          {[
            { name: t('inv_portal_dashboard') || 'Dashboard', icon: LayoutDashboard, path: '/invoices/dashboard' },
            { name: t('inv_title') || 'Invoices', icon: FileText, path: '/invoices/manage' },
            { name: t('inv_create') || 'New', icon: Plus, path: '/invoices/manage', special: true },
            { name: t('shop_nav_settings') || 'Settings', icon: Settings, path: '/invoices/settings' },
          ].map((item, idx) => (
            <Link key={idx} to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full transition-colors ${
                item.special ? '' : isActive(item.path) ? 'text-[#22c55e]' : 'text-slate-400'
              }`}>
              {item.special ? (
                <div className="w-10 h-10 bg-[#22c55e] rounded-full flex items-center justify-center -mt-4 shadow-lg">
                  <item.icon className="w-5 h-5 text-white" />
                </div>
              ) : (
                <>
                  <item.icon className="w-5 h-5" />
                  <span className="text-[10px] mt-0.5 font-medium">{item.name}</span>
                </>
              )}
            </Link>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default InvoicePortalLayout;
